import { useEffect, useRef, useState } from "react";
import { Button, Dialog, DialogContent, DialogTitle, Divider, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

import { NotesDialog } from "../../note/notes.dialog";
import { useLoadData } from "../../../hooks/useLoadData";
import { useSaveData } from "../../../hooks/useSaveData";
import { ApiResponse } from "../../../models/api-response";
import { DetailsFormComponent } from "./details-form.component";
import { useReactHookForm } from "../../../hooks/useReactHookForm";
import { PlanReviewTaskResult } from "./plan-review-task-result.component";
import { PlanDetailsComponent } from "./plan-details/plan-details.component";
import { planReviewApiKeyStore } from "../../../api/plan-review-api-key-store";
import { ReactHookForm } from "../../../components/hook-form/react-hook-form.component";
import { FileUploadComponent } from "./file-upload/file-upload.component";
import { GuardedComponent } from "../../../components/guarded-component";
import { UserPermissionName } from "../../../models/user-permission-name";
import { useAuthorize } from "../../../hooks/use-authorize";

const isPlanEnabled = process.env.REACT_APP_PLAN_FLAG === 'true';

const styling = {
    dialogTitleStyle: { padding: '16px 24px 0' },
    contentPadding: { paddingTop: '30px' },
};

interface PlanReviewTaskDialog {
    title: string;
    planReviewData: any;
    moduleCode: string;
    planReviewSchema: any;
    onSubmit: () => void;
    onClosed: () => void;
    parentRefetch: () => void;
}

export const PlanReviewTaskDialog = ({ title, planReviewData, moduleCode, planReviewSchema, onSubmit, onClosed, parentRefetch }: PlanReviewTaskDialog) => {

    const form = useReactHookForm({
        defaultValues: {
            workOrderNumber: "",
            customerName: "",
            officeName: "",
            projectName: "",
            unit: "",
            address: "",
            jobsite: "",
            conditionedArea: "",
            volume: "",
            windowArea: "",
            squareFeet: "",
            revisionNumber: "",
            currentStatus: "",
            statusCode: "",
            serviceGroupName: "",
            airflow: "",
            hvacContractor: "",
            hvacDesignFileName: "",
            hvacDesignFileType: "",
            hvacDesignFile: "",
            includeHVACGrading: false,

            accaReportFileName: "",
            accaReportFileType: "",
            accaReportFile: "",
            designedAHModel: "",
            blowerFanAirflowDesign: "",
            blowerFanAirflowDesignSetting: "",

            sys1DesignedAHModel: "",
            sys1BlowerFanAirflowDesign: "",
            sys1BlowerFanAirflowDesignSetting: "",

            sys1AirFlowResult: "",
            sys1AirFlowScoring: "",
            sys1AirFlowGrading: "",
            sys2AirFlowResult: "",
            sys2AirFlowScoring: "",
            sys2AirFlowGrading: "",
            liqLineLength: "",

            registrationNumber: "",
            hersScore: ""
        },
        yupSchema: planReviewSchema
    });

    const [file, setFile] = useState<any>();
    const [accFile, setAccFile] = useState<any>();
    const [openNoteDialog, setNoteDialog] = useState<boolean>(false);
    const [updatedStatus, setUpdatedStatus] = useState<any>();
    const [status, setStatus] = useState<string>();
    const [fileTypeData, setFileTypeData] = useState<any>();

    const { data, isLoading, isFetching, refetch } = useLoadData<ApiResponse<any>>(
        planReviewApiKeyStore.getPlanReviewTask(planReviewData?.planReviewTaskID),
        !!planReviewData?.planReviewTaskID && planReviewData?.planReviewTaskID > 0
    );

    const { isLoading: isPlanReReviewLoading, mutateAsync: mutatePlanReReviewAsync } = useSaveData<any>(planReviewApiKeyStore.updateMarkPlanReviewForReReview());

    const { isLoading: isReviewTaskLoading, mutateAsync } = useSaveData<any>(planReviewApiKeyStore.updatePlanReviewTask());

    const { isLoading: isTaskStatusLoading, mutateAsync: mutateTaskStatusAsync } = useSaveData<any>(planReviewApiKeyStore.updatePlanReviewTaskStatus(),
        () => planReviewApiKeyStore.getPlanReviewStatus(moduleCode, planReviewData?.planReviewTaskID));

    const [planIdList, setPlanIdList] = useState<any[]>(data?.data?.unitPlansList?.map((plan: any) => plan.planID) ?? []);

    useEffect(() => {
        if (data) {
            const taskData = data?.data;
            form.reset({
                workOrderNumber: taskData?.workOrderNumber,
                customerName: taskData?.customerName,
                officeName: taskData?.officeName,
                projectName: taskData?.projectName,
                unit: taskData?.unitName,
                address: taskData?.address,
                jobsite: taskData?.jobSite,
                conditionedArea: fileTypeData ? fileTypeData?.area : taskData?.achArea,
                volume: fileTypeData ? fileTypeData?.volume : taskData?.volume,
                windowArea: fileTypeData ? fileTypeData?.windowArea : taskData?.windowArea,
                squareFeet: taskData?.squareFeet,
                note: taskData?.comments,
                serviceGroupName: taskData?.serviceGroupName,
                currentStatus: taskData?.planReviewTaskStatus,
                statusCode: taskData?.planReviewTaskStatusCode,
                revisionNumber: taskData?.revisionNumber,
                airflow: taskData?.airflow,
                hvacContractor: taskData?.hvacContractor,
                hvacDesignFileName: taskData?.hvacDesignFileName ?? "",
                hvacDesignFileType: taskData?.hvacDesignFileType ?? "",
                hvacDesignFile: taskData?.hvacDesignFile ?? "",
                includeHVACGrading: taskData?.includeHVACGrading ?? false,

                accaReportFileName: taskData?.accaReportFileName ?? "",
                accaReportFileType: taskData?.accaReportFileType ?? "",
                accaReportFile: taskData?.accaReportFile ?? "",
                designedAHModel: taskData?.designedAHModel ?? "",
                blowerFanAirflowDesign: taskData?.blowerFanAirflowDesign ?? "",
                blowerFanAirflowDesignSetting: taskData?.blowerFanAirflowDesignSetting ?? "",

                weighInDataReceived: taskData?.weighInDataReceived ? taskData?.weighInDataReceived : false,

                sys1DesignedAHModel: taskData?.sys1DesignedAHModel ?? "",
                sys1BlowerFanAirflowDesign: taskData?.sys1BlowerFanAirflowDesign ?? "",
                sys1BlowerFanAirflowDesignSetting: taskData?.sys1BlowerFanAirflowDesignSetting ?? "",

                sys1AirFlowResult: taskData?.sys1AirFlowResult ?? "",
                sys1AirFlowScoring: taskData?.sys1AirFlowScoring ?? "",
                sys1AirFlowGrading: taskData?.sys1AirFlowGrading ?? "",
                sys2AirFlowResult: taskData?.sys2AirFlowResult ?? "",
                sys2AirFlowScoring: taskData?.sys2AirFlowScoring ?? "",
                sys2AirFlowGrading: taskData?.sys2AirFlowGrading ?? "",
                liqLineLength: taskData?.liqLineLength ?? "",

                registrationNumber: taskData?.registrationNumber ?? "",
                hersScore: taskData?.hersScore ?? ""
            })
            setPlanIdList(data?.data?.unitPlansList?.map((plan: any) => plan?.itemType == "Plan" && plan.planID))
        }
    }, [data, fileTypeData])

    const buttonTypeRef = useRef<string>("");

    const submitHandler = async (value: any, statusData?: any) => {
        const formData = new FormData();
        formData.append('PlanReviewTaskID', planReviewData?.planReviewTaskID ?? '');
        if (value?.windowArea || value?.conditionedArea || value?.volume) {
            formData.append('Windowarea', value?.windowArea ?? '');
            formData.append('Acharea', value?.conditionedArea ?? '');
            formData.append('Volume', value?.volume ?? '');
        }
        // if (value?.squareFeet) {
        //     formData.append('SquareFeet', value?.squareFeet ?? '');
        // }
        if (value?.hvacContractor || value?.airflow || file || value?.hvacDesignFileName) {
            // formData.append('EnergyStar', value?.energyStar ?? false);
            formData.append('HVACDesignFile', file ?? '');
            formData.append('HVACDesignFileName', file ? file?.name : value?.hvacDesignFileName);
            formData.append('HVACDesignFileType', file ? file?.type : value?.hvacDesignFileType);
            formData.append('HVACContractor', value?.hvacContractor ?? '');
            formData.append('Airflow', value?.airflow ?? '');
        }

        if (value?.designedAHModel ||
            value?.blowerFanAirflowDesign ||
            value?.blowerFanAirflowDesignSetting ||
            value?.sys1DesignedAHModel ||
            value?.sys1BlowerFanAirflowDesign ||
            value?.sys1BlowerFanAirflowDesignSetting ||
            accFile ||
            value?.accaReportFileName ||
            value?.weighInDataReceived) {
            formData.append('IncludeHVACGrading', value?.includeHVACGrading ?? false);
            formData.append('ACCAReportFile', accFile ?? '');
            formData.append('ACCAReportFileName', accFile ? accFile?.name : value?.accaReportFileName);
            formData.append('ACCAReportFileType', accFile ? accFile?.type : value?.accaReportFileType);
            formData.append('DesignedAHModel', value?.designedAHModel ?? '');
            formData.append('BlowerFanAirflowDesign', value?.blowerFanAirflowDesign ?? "");
            formData.append('BlowerFanAirflowDesignSetting', value?.blowerFanAirflowDesignSetting ?? "");
            formData.append('WeighInDataReceived', value?.weighInDataReceived ?? false);

            formData.append('Sys1DesignedAHModel', value?.sys1DesignedAHModel ?? "");
            formData.append('Sys1BlowerFanAirflowDesign', value?.sys1BlowerFanAirflowDesign ?? "");
            formData.append('Sys1BlowerFanAirflowDesignSetting', value?.sys1BlowerFanAirflowDesignSetting ?? "");
        }

        if (value?.registrationNumber) {
            formData.append('RegistrationNumber', value?.registrationNumber ?? '');
        }
        if (value?.hersScore) formData.append('HersScore', value?.hersScore ? value?.hersScore : 0);
        if (value?.revisionNumber) formData.append('RevisionNumber', value?.revisionNumber ?? '');
        formData.append('PlanReviewTaskStatusCode', status ?? value?.statusCode);

        await mutateAsync({ data: formData });

        if (buttonTypeRef.current == 'save') onSubmit();
        else {
            await mutateTaskStatusAsync({ data: statusData });
            parentRefetch();
            refetch();
        }
    };

    const handleFileData = (fileData: any) => { setFile(fileData) }
    const handleAccFileData = (fileData: any) => { setAccFile(fileData) }

    const handlePlanIds = (planIds: any[]) => {
        setPlanIdList(planIds ?? [])
    }

    const handleSubmitPlanReview = async () => {
        buttonTypeRef.current = 'save';
        await form.handleSubmit(submitHandler)();
    };

    const handleStatusSubmit = async (statusData: any) => {
        buttonTypeRef.current = 'status';
        await form.handleSubmit((value) => submitHandler(value, statusData))();
    }

    const handleStatus = (statusValue: string) => {
        setStatus(statusValue);
    }

    const handleSavedPlanData = (planTypeData: any) => {
        setFileTypeData(planTypeData);
    }

    const handlePlanReReview = async () => {
        const planReReviewPayload = {
            planReviewTaskID: data?.data?.planReviewTaskID,
            planReviewTaskStatusCode: data?.data?.planReviewTaskStatusCode
        }
        await mutatePlanReReviewAsync({ data: planReReviewPayload });
        refetch();
        onSubmit();
    }

    const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

    return (
        <Dialog
            open={true}
            fullWidth={true}
            maxWidth={'xl'}
            // onClose={handleClose}
            aria-labelledby="dialog-title"
            aria-describedby="dialog-description"
        >
            <DialogTitle id="dialog-title" style={styling.dialogTitleStyle}>
                {title}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClosed}
                sx={{
                    position: 'absolute',
                    right: 10,
                    top: 9,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <Tooltip title={<Typography variant="body1">Close</Typography>}>
                    <CloseIcon />
                </Tooltip>
            </IconButton>
            <DialogContent dividers style={styling.contentPadding} className="popupDilog">
                <ReactHookForm formName="PLAN_REVIEW_TASK_FORM" onSubmit={() => { }} formContextProps={form}>
                    <Grid container spacing={2}>
                        <Grid item lg={12} sm={12} xl={12} xs={12}>
                            <DetailsFormComponent isLoading={isLoading || isFetching || isReviewTaskLoading || isPlanReReviewLoading} />
                        </Grid>
                        {isPlanEnabled && (
                            <Grid item lg={12} sm={12} xl={12} xs={12}>
                                <PlanDetailsComponent
                                    title={title}
                                    planReviewTaskData={data?.data}
                                    moduleCode={moduleCode}
                                    setPlanIds={handlePlanIds}
                                    onSavePlan={handleSavedPlanData}
                                    isLoading={isLoading || isFetching || isReviewTaskLoading || isPlanReReviewLoading} />
                            </Grid>
                        )}
                        {moduleCode !== 'PDB_ENERGY' && (
                            <Grid item lg={12} sm={12} xl={12} xs={12}>
                                <FileUploadComponent title={title} unitID={data?.data?.unitID} />
                            </Grid>
                        )}
                        <Grid item lg={12} sm={12} xl={12} xs={12}><Divider /></Grid>
                        <Grid item lg={12} sm={12} xl={12} xs={12}>
                            <PlanReviewTaskResult
                                title={title}
                                moduleCode={moduleCode}
                                planReviewData={data?.data}
                                handleSubmitData={handleStatusSubmit}
                                passFileData={handleFileData}
                                passAccFileData={handleAccFileData}
                                parentRefetch={parentRefetch}
                                passStatusValue={handleStatus}
                                isParentLoading={isLoading || isFetching || isReviewTaskLoading || isTaskStatusLoading || isPlanReReviewLoading} />
                        </Grid>
                        <Grid item lg={12} sm={12} xl={12} xs={12}>
                            <div className="spaceBetween">
                                <div>
                                    <Button variant="outlined" style={{ width: '130px' }} onClick={onClosed}>
                                        Cancel
                                    </Button>
                                    <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_NOTE]}>
                                        <Button variant="contained" style={{ width: '130px', marginLeft: 10 }} onClick={() => setNoteDialog(true)}>
                                            NOTE
                                        </Button>
                                    </GuardedComponent>
                                </div>
                                <div>
                                    {data?.data?.showRereviewRequiredButton && moduleCode == 'PDB_PPI' && (
                                        <Button
                                            type='button'
                                            variant="contained"
                                            style={{ width: '180px', marginRight: 10 }}
                                            disabled={isLoading || isPlanReReviewLoading || isReviewTaskLoading || isReadOnlyPermission}
                                            onClick={handlePlanReReview}>
                                            Re-Review Required
                                        </Button>
                                    )}
                                    <Button
                                        type='button'
                                        variant="contained"
                                        style={{ width: '130px' }}
                                        disabled={isLoading || isPlanReReviewLoading || isReviewTaskLoading || isReadOnlyPermission}
                                        onClick={handleSubmitPlanReview}>
                                        Save
                                    </Button>
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </ReactHookForm>
            </DialogContent>
            {openNoteDialog && (
                <NotesDialog
                    noteRequestArgs={{ unitID: planReviewData?.unitID }}
                    open={openNoteDialog}
                    onClose={() => setNoteDialog(false)}
                />
            )}
        </Dialog>
    );
}